import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The bits of a lesson that live only in component state, kept across a route
 * change so leaving the dashboard loses nothing.
 *
 * WHAT IS AND IS NOT AT RISK, because most of the lesson is already safe and
 * it is worth being precise about the gap this closes:
 *  - the timer is server-stamped (`lesson.startedAt`) — survives already
 *  - song notes are written to Firestore on submit — survive already
 *  - the recap draft has a 10s localStorage backup, but `beforeunload` does
 *    NOT fire on an in-app route change, so up to 10 seconds of typing was
 *    lost by navigating away (fixed separately, in useRecapDrafts)
 *  - the ACTIVE SONG TAB and any UNSENT JOT TEXT were plain useState and were
 *    simply gone. Those are what this hook keeps.
 *
 * SESSION storage and keyed per lesson: scratch is a per-tab, per-lesson
 * convenience, and a stale draft resurfacing days later on another device
 * would be worse than losing it. Every access is guarded — a private window or
 * blocked site data throws on read, and losing scratch must never take the
 * page down with it.
 */

const keyFor = (lessonId) => `cyv.lessonScratch.${lessonId}`;

const EMPTY = { currentSongId: null, jotDraft: '', openSongIds: [], closedSongIds: [] };

const read = (lessonId) => {
	if (!lessonId) return EMPTY;
	try {
		const raw = sessionStorage.getItem(keyFor(lessonId));
		if (!raw) return EMPTY;
		const parsed = JSON.parse(raw);
		return { ...EMPTY, ...(parsed && typeof parsed === 'object' ? parsed : {}) };
	} catch {
		return EMPTY;
	}
};

const useLessonScratch = (lessonId) => {
	const [scratch, setScratch] = useState(() => read(lessonId));
	// Re-read when the lesson changes (a teacher moving straight to the next one).
	const lastLesson = useRef(lessonId);
	if (lastLesson.current !== lessonId) {
		lastLesson.current = lessonId;
	}

	useEffect(() => {
		setScratch(read(lessonId));
	}, [lessonId]);

	useEffect(() => {
		if (!lessonId) return;
		try {
			// An all-empty scratch is removed rather than stored, so a finished
			// lesson leaves nothing behind for the next one to trip over.
			const isEmpty = !scratch.currentSongId && !scratch.jotDraft && !(scratch.openSongIds || []).length && !(scratch.closedSongIds || []).length;
			if (isEmpty) sessionStorage.removeItem(keyFor(lessonId));
			else sessionStorage.setItem(keyFor(lessonId), JSON.stringify(scratch));
		} catch {
			/* best-effort, see the header */
		}
	}, [lessonId, scratch]);

	const patchScratch = useCallback((patch) => {
		setScratch((prev) => {
			const next = { ...prev, ...patch };
			// Avoid a render loop when a caller re-reports the same value.
			const same = Object.keys(patch).every((k) => prev[k] === next[k]);
			return same ? prev : next;
		});
	}, []);

	/** Wrap-up calls this: the lesson is done, the scratch is noise now. */
	const clearScratch = useCallback(() => {
		setScratch(EMPTY);
		if (!lessonId) return;
		try {
			sessionStorage.removeItem(keyFor(lessonId));
		} catch {
			/* best-effort */
		}
	}, [lessonId]);

	return { scratch, patchScratch, clearScratch };
};

export default useLessonScratch;
